import * as React from 'react';
import { userSession } from 'state/session';
import { energyLabelState } from 'state/energyLabel';
import { tenantConfig } from 'utils/tenant';
import { resolveLabelUrl, invalidateCacheEntry } from 'utils/energyLabel/resolver';
import { handleResolverFallback } from 'utils/energyLabel/fallback';
import { validateImageDimensions } from 'utils/energyLabel/validateImage';
import { getLocale } from 'i18n/locale';
import { getCountryISO2 } from 'utils/isoConverter';
import type { IBrand, IMarket, IVehicle } from 'data/schema';

const MAX_RETRIES = 3;
const RETRY_DELAYS = [2000, 4000, 8000];

interface IResolvedFor {
  vehicleId: string;
  efficiencyClass: string | undefined;
  market: string;
  brand: IBrand;
}

export function useEnergyLabelUrl(vehicleId: string | undefined): string | null {
  const session = userSession().useState();
  const labelState = energyLabelState().useState();
  tenantConfig().useState();

  const [resolvedUrl, setResolvedUrl] = React.useState<string | null>(null);
  const resolvedForRef = React.useRef<IResolvedFor | undefined>(undefined);
  const [retryCount, setRetryCount] = React.useState(0);

  const market = session.market ?? '';
  const brand = (session.brand ?? '') as IBrand;
  const isGated = tenantConfig().isAllowed('energyLabel');

  const integratorUrl = vehicleId ? (labelState.integratorUrls[vehicleId] ?? null) : null;

  const vehicle = vehicleId ? (session.vehicles?.[vehicleId] as IVehicle | undefined) : undefined;
  const efficiencyClass = vehicle?.efficiencyClass;

  React.useEffect(() => {
    if (!isGated || !vehicleId || integratorUrl) {
      setResolvedUrl(null);
      resolvedForRef.current = undefined;
      setRetryCount(0);
      return;
    }

    const ref = resolvedForRef.current;
    if (
      ref?.vehicleId === vehicleId &&
      ref?.efficiencyClass === efficiencyClass &&
      ref?.market === market &&
      ref?.brand === brand &&
      retryCount === 0
    ) {
      return;
    }

    let cancelled = false;
    const country = getCountryISO2(market) ?? '';
    const locale = getLocale(market as IMarket);
    const lang = locale.split('_')[0].toLowerCase();
    const energyLocale = country && lang ? `${country}-${lang}` : '';

    const resolve = () => {
      resolveLabelUrl(brand, { efficiencyClass, locale: energyLocale }).then(async (url) => {
        if (cancelled) return;

        if (!url) {
          setResolvedUrl(null);
          resolvedForRef.current = { vehicleId, efficiencyClass, market, brand };
          setRetryCount(0);
          handleResolverFallback();
          return;
        }

        const isValid = await validateImageDimensions(url);
        if (cancelled) return;

        if (isValid) {
          setResolvedUrl(url);
          resolvedForRef.current = { vehicleId, efficiencyClass, market, brand };
          setRetryCount(0);
          return;
        }

        if (retryCount < MAX_RETRIES) {
          if (efficiencyClass) {
            invalidateCacheEntry(efficiencyClass, energyLocale);
          }
          const delay = RETRY_DELAYS[retryCount] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
          setTimeout(() => {
            if (!cancelled) {
              setRetryCount((prev) => prev + 1);
            }
          }, delay);
        } else {
          setResolvedUrl(url);
          resolvedForRef.current = { vehicleId, efficiencyClass, market, brand };
          setRetryCount(0);
        }
      });
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [isGated, vehicleId, integratorUrl, brand, market, efficiencyClass, retryCount]);

  if (!isGated) return null;
  return integratorUrl ?? resolvedUrl;
}
